/**
 * Generates an Idempotency-Key for a mutating request. The caller is
 * responsible for holding onto the value across retries of the same logical
 * operation — minting a fresh key on retry defeats the purpose. Use
 * `useIdempotencyKey`, which handles that holding.
 *
 * Kept out of `helpers` deliberately: that module pulls in the Firebase SDK,
 * which this has no need of and which makes the consumers awkward to test.
 */
export const generateIdempotencyKey = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}-${Math.random()
    .toString(36)
    .slice(2, 12)}`;
};

/**
 * Shown when the server answered with a replay rather than processing a new
 * payment. Worth saying out loud: without it a user who deliberately paid the
 * same amount twice would see a plain success and believe both went through.
 */
export const IDEMPOTENT_REPLAY_TITLE = 'Payment already processed';
export const IDEMPOTENT_REPLAY_SUBTITLE =
  'This payment had already gone through, so we have not charged you again. Please check your Transaction History to confirm.';

/**
 * Shown when the request never got an answer — aborted, timed out, or killed by
 * a gateway. The payment may well have been processed anyway, so this must not
 * read as a failure: telling the buyer "error, try again" invites a duplicate
 * and leaves them believing nothing happened.
 *
 * The subtitle doubles as the rejection payload the payment thunks hand back,
 * so a screen that has not been taught about this case still shows a sensible
 * sentence rather than an error code.
 */
export const PAYMENT_UNCONFIRMED_TITLE = 'Payment status unconfirmed';
export const PAYMENT_UNCONFIRMED_SUBTITLE =
  'The connection dropped before we received confirmation, so this payment may still have gone through. Please check your Transaction History before trying again.';

/** True for a rejection the thunks classified as unconfirmed. */
export const isUnconfirmedPaymentError = (payload: unknown): boolean =>
  payload === PAYMENT_UNCONFIRMED_SUBTITLE;

// No response body means the server never answered; a 502/503/504 means a
// gateway gave up on a backend that may still be working. Both leave the
// payment's fate unknown. Anything else (a 4xx, a validation error) is a real
// answer and must keep its own message.
const UNCONFIRMED_ERROR_CODES = ['ECONNABORTED', 'ETIMEDOUT', 'ERR_NETWORK', 'ERR_CANCELED'];
const UNCONFIRMED_STATUSES = [502, 503, 504];

export const isUnconfirmedPaymentRequest = (error: any): boolean => {
  if (!error) {
    return false;
  }
  const status = error?.response?.status;
  if (status) {
    return UNCONFIRMED_STATUSES.includes(status);
  }
  if (UNCONFIRMED_ERROR_CODES.includes(error?.code)) {
    return true;
  }
  return (
    error?.name === 'AbortError' ||
    error?.name === 'CanceledError' ||
    error?.message === 'Network Error'
  );
};
